// Pages/Admin/ReserveException.jsx
import React, { useState, useEffect, useCallback } from "react";
import { Button, Chip, Divider } from "@mui/material";
import {
  fetchAvailableDates,
  fetchAvailableMonths,
  fetchAvailableSlots,
  AddReserveSlotException,
  getReserveSlotExceptions,
  removeReserveSlotException,
} from "../../Services/ReservationService";
import Loader from "../../Components/Core/Loader";
import ReservaDateTimeItme from "../../Components/Reservation/ReservaDateTimeItme";
import ReserveSlotItme from "../../Components/Reservation/ReserveSlotItme";
import ExceptionSlotItem from "../../Components/Reservation/ExceptionSlotItem";
import Notification from "../../Components/Core/Notification";
import ConfirmModal from "../../Components/Modals/ConfirmModal";
import { formatDate } from "../../Services/GeneralService";
import ReservaMonthItem from "../../Components/Reservation/ReservaMonthItem";
import InfoIcon from "@mui/icons-material/Info";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";

const ReserveException = () => {
  const [availableDates, setAvailableDates] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [exceptionSlots, setExceptionSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slotLoading, setSlotLoading] = useState(false);
  const [dayLoading, setDayLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openRestoreDialog, setOpenRestoreDialog] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [deleteReserveSlots, setDeleteReserveSlots] = useState([]);
  const [selectedExceptionId, setSelectedExceptionId] = useState(null);

  const FetchAvailableMonths = useCallback(async () => {
    try {
      const months = await fetchAvailableMonths();
      setAvailableMonths(months);
      if (months.length > 0) {
        handleMonthClick(months[0]);
      }
    } catch (error) {
      console.error(error);
      setSnackbarSeverity("error");
      setSnackbarMessage("خطا در دریافت ماه‌های موجود");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMonthClick = async (MonthVM) => {
    try {
      setDayLoading(true);
      setSelectedMonth(MonthVM);
      setSelectedDate(null);
      setExceptionSlots([]);
      const availableDates = await fetchAvailableDates(MonthVM);
      setAvailableDates(availableDates);
      if (availableDates.length > 0) {
        handleDateClick(availableDates[0].date, availableDates[0].dateDisplay);
      }
    } catch (error) {
      console.error(error);
      setSnackbarSeverity("error");
      setSnackbarMessage("خطا در دریافت روزهای موجود");
      setOpenSnackbar(true);
    } finally {
      setDayLoading(false);
    }
  };

  const handleDateClick = async (date) => {
    setDeleteReserveSlots([]);
    setSelectedDate(date);
    setSlotLoading(true);
    try {
      const [slots, exceptions] = await Promise.all([
        fetchAvailableSlots(date),
        getReserveSlotExceptions(date),
      ]);
      setAvailableSlots(slots);
      setExceptionSlots(exceptions);
    } catch (error) {
      console.error(error);
      setSnackbarSeverity("error");
      setSnackbarMessage("خطا در دریافت تایم‌های موجود");
      setOpenSnackbar(true);
    } finally {
      setSlotLoading(false);
    }
  };

  const handleSlotClick = (slot) => {
    if (
      deleteReserveSlots.some(
        (itm) => itm.StartTime === slot && itm.Date === formatDate(selectedDate)
      )
    ) {
      const newDeleteReserveSlots = deleteReserveSlots.filter(
        (itm) => itm.StartTime !== slot
      );
      setDeleteReserveSlots(newDeleteReserveSlots);
    } else {
      setDeleteReserveSlots([
        ...deleteReserveSlots,
        {
          Date: formatDate(selectedDate),
          StartTime: slot,
        },
      ]);
    }
  };

  const handleOpenDeleteDialog = () => {
    setOpenDialog(true);
  };

  const handleOpenRestoreDialog = (exceptionId) => {
    setSelectedExceptionId(exceptionId);
    setOpenRestoreDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteReserveSlots.length > 0) {
      setOpenDialog(false);
      try {
        setLoading(true);
        await AddReserveSlotException(deleteReserveSlots);
        setSnackbarMessage(
          `${deleteReserveSlots.length} تایم رزرو با موفقیت حذف شد`
        );
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
        setDeleteReserveSlots([]);
        await handleDateClick(selectedDate);
      } catch (error) {
        setSnackbarMessage(error.message);
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleConfirmRestore = async () => {
    setOpenRestoreDialog(false);
    try {
      setLoading(true);
      await removeReserveSlotException(selectedExceptionId);
      setSnackbarMessage("تایم رزرو با موفقیت بازگردانی شد");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      await handleDateClick(selectedDate);
    } catch (error) {
      setSnackbarMessage(error.message);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
      setSelectedExceptionId(null);
    }
  };

  useEffect(() => {
    document.title = "مدیریت تایم‌های رزرو";
    FetchAvailableMonths();
  }, [FetchAvailableMonths]);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div className="container-fluid px-4 mb-5 pb-5">
          {/* Header Section */}
          <div className="row mt-4 mb-4">
            <div className="col-12">
              <div
                className="p-4 rounded-4"
                style={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  boxShadow: "0 4px 20px rgba(102, 126, 234, 0.4)",
                }}
              >
                <h2 className="text-white fw-bold mb-2 iran-sans">
                  <DeleteSweepIcon className="me-2" />
                  مدیریت تایم‌های رزرو
                </h2>
                <p className="text-white-50 mb-0 iran-sans">
                  در این بخش می‌توانید تایم‌های رزرو را مدیریت کنید
                </p>
              </div>
            </div>
          </div>

          {/* Info Alert */}
          <div className="row mb-4">
            <div className="col-12">
              <div
                className="p-3 rounded-3 d-flex align-items-center"
                style={{
                  background: "rgba(59, 130, 246, 0.1)",
                  border: "1px solid rgba(59, 130, 246, 0.3)",
                }}
              >
                <InfoIcon
                  style={{ color: "#3b82f6", fontSize: "24px" }}
                  className="me-3"
                />
                <div className="iran-sans" style={{ fontSize: "0.95rem" }}>
                  <strong>راهنما:</strong> تایم‌های قرمز نشان‌دهنده تایم‌های حذف
                  شده هستند. می‌توانید با کلیک روی دکمه بازگردانی، آن‌ها را فعال
                  کنید.
                </div>
              </div>
            </div>
          </div>

          {/* Month Selection */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="d-flex align-items-center mb-3">
                <h5 className="fw-bold iran-sans color-blue mb-0">
                  انتخاب ماه
                </h5>
                <Chip
                  label={`${availableMonths.length} ماه موجود`}
                  size="small"
                  className="me-3 iran-sans"
                  style={{
                    background: "#7cc890",
                    color: "white",
                    fontWeight: "bold",
                  }}
                />
              </div>
              <div className="overflow-scroll custom-scroll pb-2">
                <div className="d-flex gap-3">
                  {availableMonths.map((itm, index) => (
                    <ReservaMonthItem
                      selectedMonth={selectedMonth}
                      itm={itm}
                      handleDateClick={handleMonthClick}
                      key={index}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {selectedMonth && (
            <>
              {dayLoading ? (
                <Loader />
              ) : (
                <>
                  {/* Day Selection */}
                  <div className="row mb-4">
                    <div className="col-12">
                      <div className="d-flex align-items-center mb-3">
                        <h5 className="fw-bold iran-sans color-blue mb-0">
                          انتخاب روز
                        </h5>
                        <Chip
                          label={`${availableDates.length} روز موجود`}
                          size="small"
                          className="me-3 iran-sans"
                          style={{
                            background: "#7cc890",
                            color: "white",
                            fontWeight: "bold",
                          }}
                        />
                      </div>
                      <div className="overflow-scroll pb-1">
                        <div className="d-flex gap-3">
                          {availableDates.map((itm, index) => (
                            <ReservaDateTimeItme
                              selectedDate={selectedDate}
                              itm={itm}
                              handleDateClick={handleDateClick}
                              key={index}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {selectedDate && (
                    <>
                      {slotLoading ? (
                        <Loader />
                      ) : (
                        <>
                          {/* Statistics Cards */}
                          <div className="row mb-4">
                            <div className="col-md-4 mb-3">
                              <div
                                className="p-3 rounded-3 text-center"
                                style={{
                                  background:
                                    "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
                                  boxShadow:
                                    "0 4px 15px rgba(132, 250, 176, 0.3)",
                                }}
                              >
                                <div
                                  className="fw-bold text-white"
                                  style={{ fontSize: "2rem" }}
                                >
                                  {availableSlots.length}
                                </div>
                                <div
                                  className="iran-sans text-white"
                                  style={{ fontSize: "0.9rem" }}
                                >
                                  تایم فعال
                                </div>
                              </div>
                            </div>

                            <div className="col-md-4 mb-3">
                              <div
                                className="p-3 rounded-3 text-center"
                                style={{
                                  background:
                                    "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                                  boxShadow:
                                    "0 4px 15px rgba(250, 112, 154, 0.3)",
                                }}
                              >
                                <div
                                  className="fw-bold text-white"
                                  style={{ fontSize: "2rem" }}
                                >
                                  {exceptionSlots.length}
                                </div>
                                <div
                                  className="iran-sans text-white"
                                  style={{ fontSize: "0.9rem" }}
                                >
                                  تایم حذف شده
                                </div>
                              </div>
                            </div>

                            <div className="col-md-4 mb-3">
                              <div
                                className="p-3 rounded-3 text-center"
                                style={{
                                  background:
                                    "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
                                  boxShadow:
                                    "0 4px 15px rgba(168, 237, 234, 0.3)",
                                }}
                              >
                                <div
                                  className="fw-bold text-white"
                                  style={{ fontSize: "2rem" }}
                                >
                                  {deleteReserveSlots.length}
                                </div>
                                <div
                                  className="iran-sans text-white"
                                  style={{ fontSize: "0.9rem" }}
                                >
                                  انتخاب شده برای حذف
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Available Slots Section */}
                          {availableSlots.length > 0 && (
                            <>
                              <Divider className="my-4">
                                <Chip
                                  label="تایم‌های فعال"
                                  className="iran-sans fw-bold"
                                  style={{
                                    background: "#7cc890",
                                    color: "white",
                                    padding: "20px 15px",
                                    fontSize: "1rem",
                                  }}
                                />
                              </Divider>

                              <div className="row g-3 mb-4">
                                {availableSlots.map((slot, index) => (
                                  <ReserveSlotItme
                                    selectedSlot={
                                      deleteReserveSlots.some(
                                        (itm) =>
                                          itm.StartTime === slot &&
                                          itm.Date === formatDate(selectedDate)
                                      )
                                        ? slot
                                        : null
                                    }
                                    slot={slot}
                                    handleSlotClick={handleSlotClick}
                                    key={index}
                                  />
                                ))}
                              </div>
                            </>
                          )}

                          {/* Exception Slots Section */}
                          {exceptionSlots.length > 0 && (
                            <>
                              <Divider className="my-4">
                                <Chip
                                  label="تایم‌های حذف شده"
                                  className="iran-sans fw-bold"
                                  style={{
                                    background: "#ef4444",
                                    color: "white",
                                    padding: "20px 15px",
                                    fontSize: "1rem",
                                  }}
                                />
                              </Divider>

                              <div className="row g-3 mb-4">
                                {exceptionSlots.map((slot, index) => (
                                  <ExceptionSlotItem
                                    key={index}
                                    slot={slot}
                                    onRestore={handleOpenRestoreDialog}
                                  />
                                ))}
                              </div>
                            </>
                          )}

                          {/* No Slots Message */}
                          {availableSlots.length === 0 &&
                            exceptionSlots.length === 0 && (
                              <div className="row">
                                <div className="col-12">
                                  <div
                                    className="p-5 text-center rounded-3"
                                    style={{
                                      background: "rgba(156, 163, 175, 0.1)",
                                      border:
                                        "2px dashed rgba(156, 163, 175, 0.3)",
                                    }}
                                  >
                                    <InfoIcon
                                      style={{
                                        fontSize: "64px",
                                        color: "#9ca3af",
                                        marginBottom: "16px",
                                      }}
                                    />
                                    <h5 className="iran-sans color-blue">
                                      در این روز هیچ تایم رزروی موجود نیست
                                    </h5>
                                  </div>
                                </div>
                              </div>
                            )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}

          {/* Action Button */}
          {deleteReserveSlots.length > 0 && (
            <div
              className="row mb-5 sticky-bottom"
              style={{ paddingBottom: "20px" }}
            >
              <div className="col-12">
                <div
                  className="p-3 rounded-3 d-flex justify-content-between align-items-center"
                  style={{
                    background: "white",
                    boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <div className="iran-sans">
                    <span>{deleteReserveSlots.length} اسلات </span>
                    <span className="me-2">انتخاب شده برای حذف</span>
                  </div>
                  <Button
                    variant="contained"
                    color="error"
                    size="large"
                    onClick={handleOpenDeleteDialog}
                    startIcon={<DeleteSweepIcon />}
                    className="iran-sans"
                    sx={{
                      borderRadius: "12px",
                      padding: "12px 32px",
                      fontSize: "1rem",
                      fontWeight: "bold",
                      boxShadow: "0 4px 15px rgba(239, 68, 68, 0.4)",
                      "&:hover": {
                        boxShadow: "0 6px 20px rgba(239, 68, 68, 0.5)",
                      },
                      fontFamily: "iran-sans, MyPersianFont, sans-serif",
                    }}
                  >
                    حذف تایم‌های انتخاب شده
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        handleCloseDialog={() => setOpenDialog(false)}
        handleConfirm={handleConfirmDelete}
        text={[
          `آیا مطمئن هستید که می‌خواهید ${deleteReserveSlots.length} تایم رزرو را حذف کنید؟`,
          "این عملیات قابل بازگشت است و می‌توانید بعداً آن‌ها را بازگردانی کنید.",
        ]}
        openDialog={openDialog}
        confirmText={"بله، حذف شود"}
        cancleText={"انصراف"}
        title={"حذف تایم‌های رزرو"}
      />

      {/* Restore Confirmation Modal */}
      <ConfirmModal
        handleCloseDialog={() => {
          setOpenRestoreDialog(false);
          setSelectedExceptionId(null);
        }}
        handleConfirm={handleConfirmRestore}
        text={[
          "آیا مطمئن هستید که می‌خواهید این تایم رزرو را بازگردانی کنید؟",
          "پس از بازگردانی، این تایم دوباره قابل رزرو خواهد بود.",
        ]}
        openDialog={openRestoreDialog}
        confirmText={"بله، بازگردانی شود"}
        cancleText={"انصراف"}
        title={"بازگردانی تایم رزرو"}
      />

      {/* Notification */}
      <Notification
        openSnackbar={openSnackbar}
        snackbarMessage={snackbarMessage}
        snackbarSeverity={snackbarSeverity}
        setOpenSnackbar={setOpenSnackbar}
      />
    </>
  );
};

export default ReserveException;
